import React, { useState, useEffect } from 'react';
import { FaChartLine, FaUserGraduate, FaUsers, FaCalendarAlt, FaChalkboardTeacher, FaFileAlt, FaExclamationTriangle } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../common/LoadingSpinner';
import { formatDate } from '../../utils/helpers';
import SubscriptionAlert from '../dashboard/SubscriptionAlert';

/**
 * School dashboard component for administrators
 * Displays key metrics, enrollment data, and recent activity
 */
const SchoolDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalStudents: 0,
      totalTeachers: 0,
      totalClasses: 0,
      averageAttendance: 0,
      performanceScore: 0
    },
    enrollment: {
      monthlyData: [],
      currentMonth: 0,
      previousMonth: 0,
      percentageChange: 0
    },
    performanceMetrics: {
      subjects: [],
      averageScores: []
    },
    classDistribution: [],
    recentActivity: []
  });
  const [errors, setErrors] = useState({});
  const { showToast, showError } = useToast();

  // Fetch dashboard data on component mount
  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Fetch dashboard data from API
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setErrors({});
      
      // Use the new dashboard endpoint that doesn't require a school ID
      const response = await api.get('/schools/dashboard');
      const responseData = response.data.data || {};
      
      // Track missing data
      const missingData = {};
      
      // Check for missing data
      if (!responseData.stats) missingData.stats = true;
      if (!responseData.enrollment) missingData.enrollment = true;
      if (!responseData.performanceMetrics) missingData.performance = true;
      if (!responseData.classDistribution) missingData.classes = true;
      if (!responseData.recentActivity) missingData.activity = true;
      
      // Ensure all data is of the proper type before setting state
      setDashboardData({
        stats: {
          totalStudents: Number(responseData.totalStudents || 0),
          totalTeachers: Number(responseData.totalTeachers || 0),
          totalClasses: Number(responseData.totalClasses || 0),
          averageAttendance: Number(responseData.averageAttendance || 0),
          performanceScore: Number(responseData.performanceScore || 0)
        },
        enrollment: responseData.enrollment ? {
          monthlyData: Array.isArray(responseData.enrollment.monthlyData) ? responseData.enrollment.monthlyData : [],
          currentMonth: Number(responseData.enrollment.currentMonth || 0),
          previousMonth: Number(responseData.enrollment.previousMonth || 0),
          percentageChange: Number(responseData.enrollment.percentageChange || 0)
        } : {
          monthlyData: [],
          currentMonth: 0,
          previousMonth: 0,
          percentageChange: 0
        },
        performanceMetrics: responseData.performanceMetrics ? {
          subjects: Array.isArray(responseData.performanceMetrics.subjects) ? responseData.performanceMetrics.subjects : [],
          averageScores: Array.isArray(responseData.performanceMetrics.averageScores) ? responseData.performanceMetrics.averageScores : []
        } : {
          subjects: [],
          averageScores: []
        },
        classDistribution: Array.isArray(responseData.classDistribution) ? responseData.classDistribution : [],
        recentActivity: Array.isArray(responseData.recentActivity) ? responseData.recentActivity : []
      });
      
      // Set errors if any data is missing
      if (Object.keys(missingData).length > 0) {
        setErrors({
          type: 'incomplete',
          missingData,
          message: 'Some dashboard data is unavailable.'
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      showError('Failed to load dashboard data');
      
      if (error.response) {
        setErrors({ 
          type: 'serverError', 
          message: 'Server error: ' + (error.response.data?.message || 'Unable to retrieve data.'),
          allMissing: true
        });
      } else if (error.request) {
        setErrors({ 
          type: 'connectionError', 
          message: 'Cannot connect to the server. Please check your network connection.',
          allMissing: true
        });
      } else {
        setErrors({ 
          type: 'unknownError', 
          message: error.message || 'An unexpected error occurred while loading data.',
          allMissing: true
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Render full error state if no data is available
  if (!loading && errors.allMissing) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">School Dashboard</h1>
            <p className="text-gray-600 mt-1">
              Overview of your school's performance and metrics
            </p>
          </div>
        </div>

        <SubscriptionAlert />
        
        <div className="bg-white rounded-lg shadow-soft p-8 text-center mt-6">
          <FaExclamationTriangle className="text-yellow-500 text-5xl mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">
            {errors.type === 'connectionError' ? 'Connection Error' : 'Data Unavailable'}
          </h2>
          <p className="text-gray-600 mb-4">{errors.message}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary inline-flex items-center"
          >
            <FaChartLine className="mr-2" /> Retry Loading
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">School Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Overview of your school's performance and metrics
          </p>
        </div>
      </div>

      {/* Subscription Alert */}
      <SubscriptionAlert />
      
      {/* Global error message */}
      {errors.type && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <FaExclamationTriangle className="text-yellow-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">{errors.message}</p>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <>
          {/* Stats Overview */}
          <div className="mb-6">
            {errors.missingData?.stats && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
                <p className="text-sm text-yellow-700 flex items-center">
                  <FaExclamationTriangle className="text-yellow-500 mr-2" />
                  School statistics are unavailable
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {/* Total Students */}
              <div className="bg-white rounded-lg shadow-soft p-6">
                <div className="flex items-center">
                  <div className="rounded-full bg-blue-100 p-3 mr-4">
                    <FaUserGraduate className="text-blue-500 text-xl" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Students</p>
                    <h3 className="text-2xl font-bold">{dashboardData.stats.totalStudents}</h3>
                  </div>
                </div>
              </div>

              {/* Total Teachers */}
              <div className="bg-white rounded-lg shadow-soft p-6">
                <div className="flex items-center">
                  <div className="rounded-full bg-green-100 p-3 mr-4">
                    <FaChalkboardTeacher className="text-green-500 text-xl" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Teachers</p>
                    <h3 className="text-2xl font-bold">{dashboardData.stats.totalTeachers}</h3>
                  </div>
                </div>
              </div>

              {/* Total Classes */}
              <div className="bg-white rounded-lg shadow-soft p-6">
                <div className="flex items-center">
                  <div className="rounded-full bg-purple-100 p-3 mr-4">
                    <FaUsers className="text-purple-500 text-xl" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Classes</p>
                    <h3 className="text-2xl font-bold">{dashboardData.stats.totalClasses}</h3>
                  </div>
                </div>
              </div>

              {/* Average Attendance */}
              <div className="bg-white rounded-lg shadow-soft p-6">
                <div className="flex items-center">
                  <div className="rounded-full bg-yellow-100 p-3 mr-4">
                    <FaCalendarAlt className="text-yellow-500 text-xl" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Attendance</p>
                    <h3 className="text-2xl font-bold">{dashboardData.stats.averageAttendance}%</h3>
                  </div>
                </div>
              </div>

              {/* Performance Score */}
              <div className="bg-white rounded-lg shadow-soft p-6">
                <div className="flex items-center">
                  <div className="rounded-full bg-red-100 p-3 mr-4">
                    <FaChartLine className="text-red-500 text-xl" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Performance</p>
                    <h3 className="text-2xl font-bold">{dashboardData.stats.performanceScore}%</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Enrollment Trend */}
            <div className="lg:col-span-2 bg-white rounded-lg shadow-soft">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="font-semibold text-lg">Monthly Enrollment Trend</h2>
              </div>
              
              {errors.missingData?.enrollment && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 m-4">
                  <p className="text-sm text-yellow-700 flex items-center">
                    <FaExclamationTriangle className="text-yellow-500 mr-2" />
                    Enrollment data is unavailable
                  </p>
                </div>
              )}
              
              <div className="p-6">
                {errors.missingData?.enrollment || dashboardData.enrollment.monthlyData.length === 0 ? (
                  <div className="text-center py-8">
                    {errors.missingData?.enrollment ? (
                      <>
                        <FaExclamationTriangle className="text-yellow-500 text-3xl mx-auto mb-2" />
                        <p className="text-gray-500">Enrollment data unavailable</p>
                      </>
                    ) : (
                      <p className="text-gray-500">No enrollment data available</p>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <span className="text-sm text-gray-500">Current Month</span>
                        <div className="text-xl font-bold">{dashboardData.enrollment.currentMonth} students</div>
                      </div>
                      <div className="text-right">
                        <span className="text-sm text-gray-500">Previous Month</span>
                        <div className="text-xl font-bold">{dashboardData.enrollment.previousMonth} students</div>
                      </div>
                      <div className="text-right">
                        <span className="text-sm text-gray-500">Change</span>
                        <div className={`text-xl font-bold ${dashboardData.enrollment.percentageChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {dashboardData.enrollment.percentageChange >= 0 ? '+' : ''}{dashboardData.enrollment.percentageChange}%
                        </div>
                      </div>
                    </div>
                    
                    {/* Enrollment Chart (Simple Representation) */}
                    <div className="h-48 flex items-end space-x-2">
                      {dashboardData.enrollment.monthlyData.map((month, index) => {
                        // Calculate the maximum count safely
                        const maxCount = Math.max(...dashboardData.enrollment.monthlyData.map(m => m.count || 0), 1);
                        const heightPercentage = ((month.count || 0) / maxCount) * 100;
                        
                        return (
                          <div key={index} className="flex-1 flex flex-col items-center">
                            <div 
                              className="w-full bg-blue-500 rounded-t" 
                              style={{ 
                                height: `${heightPercentage}%`,
                                minHeight: '10%'
                              }}
                            ></div>
                            <div className="text-xs mt-2 text-gray-500">{typeof month.name === 'string' ? month.name : String(month.name)}</div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Class Distribution */}
            <div className="bg-white rounded-lg shadow-soft">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="font-semibold text-lg">Class Distribution</h2>
              </div>
              
              {errors.missingData?.classes && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 m-4">
                  <p className="text-sm text-yellow-700 flex items-center">
                    <FaExclamationTriangle className="text-yellow-500 mr-2" />
                    Class distribution data is unavailable
                  </p>
                </div>
              )}
              
              <div className="p-6">
                {errors.missingData?.classes || dashboardData.classDistribution.length === 0 ? (
                  <div className="text-center py-8">
                    {errors.missingData?.classes ? (
                      <>
                        <FaExclamationTriangle className="text-yellow-500 text-3xl mx-auto mb-2" />
                        <p className="text-gray-500">Class distribution data unavailable</p>
                      </>
                    ) : (
                      <p className="text-gray-500">No class distribution data available</p>
                    )}
                  </div>
                ) : (
                  <>
                    {dashboardData.classDistribution.map((classInfo, index) => (
                      <div key={index} className="mb-4 last:mb-0">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium">{typeof classInfo.name === 'string' ? classInfo.name : String(classInfo.name)}</span>
                          <span className="text-sm text-gray-500">{classInfo.count} students</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div 
                            className="bg-primary h-2.5 rounded-full" 
                            style={{ width: `${dashboardData.stats.totalStudents > 0 ? ((classInfo.count || 0) / dashboardData.stats.totalStudents) * 100 : 0}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Subject Performance */}
            <div className="bg-white rounded-lg shadow-soft">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="font-semibold text-lg">Subject Performance</h2>
              </div>
              
              {errors.missingData?.performance && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 m-4">
                  <p className="text-sm text-yellow-700 flex items-center">
                    <FaExclamationTriangle className="text-yellow-500 mr-2" />
                    Performance data is unavailable
                  </p>
                </div>
              )}
              
              <div className="p-6">
                {errors.missingData?.performance || 
                 dashboardData.performanceMetrics.subjects.length === 0 ? (
                  <div className="text-center py-8">
                    {errors.missingData?.performance ? (
                      <>
                        <FaExclamationTriangle className="text-yellow-500 text-3xl mx-auto mb-2" />
                        <p className="text-gray-500">Performance data unavailable</p>
                      </>
                    ) : (
                      <p className="text-gray-500">No performance data available</p>
                    )}
                  </div>
                ) : (
                  <>
                    {dashboardData.performanceMetrics.subjects.map((subject, index) => {
                      const score = dashboardData.performanceMetrics.averageScores[index] || 0;
                      let colorClass = 'bg-red-500';
                      if (score >= 80) {
                        colorClass = 'bg-green-500';
                      } else if (score >= 60) {
                        colorClass = 'bg-blue-500';
                      } else if (score >= 40) {
                        colorClass = 'bg-yellow-500';
                      }
                      
                      return (
                        <div key={index} className="mb-4 last:mb-0">
                          <div className="flex justify-between mb-1">
                            <span className="text-sm font-medium">{subject}</span>
                            <span className="text-sm text-gray-500">{score}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div 
                              className={`${colorClass} h-2.5 rounded-full`} 
                              style={{ width: `${score}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-soft">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="font-semibold text-lg">Recent Activity</h2>
              </div>
              
              {errors.missingData?.activity && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 m-4">
                  <p className="text-sm text-yellow-700 flex items-center">
                    <FaExclamationTriangle className="text-yellow-500 mr-2" />
                    Activity data is unavailable
                  </p>
                </div>
              )}
              
              <div className="p-6">
                {errors.missingData?.activity ? (
                  <div className="text-center py-8">
                    <FaExclamationTriangle className="text-yellow-500 text-3xl mx-auto mb-2" />
                    <p className="text-gray-500">Activity data unavailable</p>
                  </div>
                ) : dashboardData.recentActivity.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No recent activity</p>
                ) : (
                  <div className="space-y-4">
                    {dashboardData.recentActivity.map((activity, index) => (
                      <div key={index} className="flex">
                        <div className="mr-4">
                          {activity.type === 'enrollment' && (
                            <div className="rounded-full bg-blue-100 p-2">
                              <FaUserGraduate className="text-blue-500" />
                            </div>
                          )}
                          {activity.type === 'performance' && (
                            <div className="rounded-full bg-green-100 p-2">
                              <FaChartLine className="text-green-500" />
                            </div>
                          )}
                          {activity.type === 'attendance' && (
                            <div className="rounded-full bg-yellow-100 p-2">
                              <FaCalendarAlt className="text-yellow-500" />
                            </div>
                          )}
                          {activity.type === 'document' && (
                            <div className="rounded-full bg-purple-100 p-2">
                              <FaFileAlt className="text-purple-500" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                            <p className="font-medium text-gray-900">{typeof activity.message === 'string' ? activity.message : String(activity.message)}</p>
                            <span className="text-sm text-gray-500">
                              {activity.date ? formatDate(activity.date) : 'No date available'}
                            </span>
                          </div>
                          {activity.details && (
                            <p className="text-sm text-gray-500 mt-1">
                              {typeof activity.details === 'object' 
                                ? JSON.stringify(activity.details) 
                                : String(activity.details)}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SchoolDashboard;
